import React, { createContext, useContext, useEffect, useState } from "react";
import { getAllCategoryNameData } from "services/HomeService";
import Cookies from "js-cookie";

interface OnlineOrderSettings {
    logo?: string;
    orderLeadTime?: number;
    image?: string;
    titleText?: string;
    customTextOne?: string;
    pauseOnlineOrder?: boolean;
    pauseOnlineOrderLabel?: string;
    restaurantId?: string;
    phn?: string;
    /** Authorize.net Accept.js – from menu API; used to get payment nonce via Accept.dispatchData() */
    publicClientKey?: string;
    apiLogInId?: string;
    /** Basil Auth.Net – for delivery fees/tips; used for second Accept.js tokenization on delivery */
    basilPublicClientKey?: string;
    basilApiLogInId?: string;
    /** Online order fee */
    onlineOrderFee?: number;
    onlineOrderFeeLabel?: string;
    /** Kanopy Payment Token JS – gateway type and device GUIDs */
    onlinePaymentGateway?: string;   // "AUTHORIZE_NET" | "KANOPY"
    kanopyApiKey?: string;
    kanopyDeviceGuid?: string;
    basilKanopyApiKey?: string;
    basilKanopyDeviceGuid?: string;
}

interface ResponseData {
    onlineOrderSettings: OnlineOrderSettings;
    items: any[];
    restaurantId?: string;
    onlinePaymentGateway?: string;
    // Historic top-level credential fields — kept for back-compat with
    // pre-migration menu responses, but the current backend no longer
    // returns them (credentials come from /onlineOrder/payment/credentials).
    publicClientKey?: string;
    apiLogInId?: string;
    basilPublicClientKey?: string;
    basilApiLogInId?: string;
    kanopyApiKey?: string;
    kanopyDeviceGuid?: string;
    basilKanopyApiKey?: string;
    basilKanopyDeviceGuid?: string;
}

interface ContextProps {
    settings: OnlineOrderSettings | null;
    items: any[];
    isLoading: boolean;
}

const OnlineOrderSettingsContext = createContext<ContextProps>({
    settings: null,
    items: [],
    isLoading: true,
});

export const useOnlineOrderSettings = () => useContext(OnlineOrderSettingsContext);

// Valid payment gateway values - the backend only sends these two.
// Validates sessionStorage to prevent XSS-injected values from being trusted.
const VALID_GATEWAYS = ["AUTHORIZE_NET", "KANOPY"] as const;

/** Type-safe read from sessionStorage with validation. Returns "" if missing or invalid. */
function readSessionValue(key: string): string {
    try {
        return sessionStorage.getItem(key)?.trim() ?? "";
    } catch {
        return ""; // sessionStorage unavailable (e.g. incognito in some browsers)
    }
}

// Kanopy gateway helpers - read values stored by OnlineOrderSettingsProvider from the menu API.
// The backend is the source of truth; these sessionStorage values are set from the /menu response.
export function isKanopyGateway(): boolean {
    // Real gateway routing — Kanopy for merchants configured with
    // onlinePaymentGateway = "KANOPY", Auth.net (Accept.js) for everyone
    // else. Restored after the temporary Kanopy-only lockdown because a
    // subset of merchants are still on Auth.net for pickup orders.
    const gw = readSessionValue("onlinePaymentGateway");
    return (VALID_GATEWAYS as readonly string[]).includes(gw) && gw === "KANOPY";
}
export function getKanopyApiKey(): string {
    return readSessionValue("kanopyApiKey");
}
export function getKanopyDeviceGuid(): string {
    return readSessionValue("kanopyDeviceGuid");
}
export function getBasilKanopyApiKey(): string {
    return readSessionValue("basilKanopyApiKey");
}
export function getBasilKanopyDeviceGuid(): string {
    return readSessionValue("basilKanopyDeviceGuid");
}

export const OnlineOrderSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<OnlineOrderSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState<any[]>([]);

    const setFavicon = (imageUrl: any) => {
        const link = document.querySelector("link[rel='icon']");
        if (link && (link as HTMLLinkElement).href !== undefined) {
            (link as HTMLLinkElement).href = imageUrl;
        }
    };
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await getAllCategoryNameData();
                const resData = response?.data as ResponseData | undefined;
                const data = resData?.onlineOrderSettings;
                const dataCategories = resData?.items ?? [];
                if (data) {
                    if (data?.logo) sessionStorage.setItem("onlineOrderSettingsLogo", data.logo);
                    if (data?.titleText) Cookies.set("onlineOrderSettingsTitleText", data?.titleText);
                    if (data?.orderLeadTime) Cookies.set("onlineOrderSettingsLeadTime", data?.orderLeadTime.toString());
                    setSettings(data);
                    setItems(dataCategories);
                    setFavicon(data?.logo);
                }
                // Gateway routing flag — still needed so isKanopyGateway()
                // can decide which tokenizer to load. Only piece of payment
                // config that stays in the menu response.
                const gateway = data?.onlinePaymentGateway ?? resData?.onlinePaymentGateway;
                if (gateway) sessionStorage.setItem("onlinePaymentGateway", gateway);

                // restaurantId — the delivery-merchant identifier used when
                // hitting /orderOut/delivery/quotes and /orderOut/delivery/orders.
                // Replaces the older `onlineDeliveryId` (now returned empty).
                const restaurantId = data?.restaurantId ?? resData?.restaurantId;
                if (restaurantId) Cookies.set("restaurantId", String(restaurantId));

                // NOTE: Accept.js and Kanopy credentials are no longer part of
                // /menu — they're issued on-demand by /onlineOrder/payment/credentials
                // at checkout time. See submitOrder() in CheckoutPage.tsx.
            } catch (error) {
                console.error("Failed to fetch online order settings", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, []);

    return (
        <OnlineOrderSettingsContext.Provider value={{ settings, items, isLoading }}>
            {children}
        </OnlineOrderSettingsContext.Provider>
    );
};
